import { memo, useCallback, useMemo, useState } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { useDailyTotalsAllProjects, useGrandTotalHours, useTotalAllocation } from '../state';
import { bigZero } from '../../../math/math';
import type Big from 'big.js';
import { useInitialData } from '../../../hooks/useInitialData';
import { useTimeGridStyles } from '../useTimeGridStyles';
import { useDateColumnStyles } from '../hooks/useDateStyles';
import { isWorkDay } from '../utils/isWorkDay';
import { TotalAllocationDialog } from './TotalAllocationDialog';
import { AvailabilityIndicator } from './AvailabilityIndicator';
import { InfoRegular } from '@fluentui/react-icons';

type TotalRowProps = {
    dates: string[];
    onAllocationProjectClick?: (projectName: string) => void;
};

const blankZero = (val: number | null): string => (val === 0 || val == null ? '' : val.toString());

type TotalDateCellProps = {
    total: Big;
    className: string;
    showIndicator: boolean;
    availability: number;
};

const TotalDateCell = memo(function TotalDateCell({ total, className, showIndicator, availability }: TotalDateCellProps) {
    return (
        <div className={className} role="gridcell">
            {showIndicator && <AvailabilityIndicator total={total} availability={availability} />}
            {total.toString()}
        </div>
    );
});

export const TotalRowComponent = memo(function TotalRowComponent({ dates, onAllocationProjectClick }: TotalRowProps) {
    const dailyTotals = useDailyTotalsAllProjects();
    const grandTotal = useGrandTotalHours();
    const totalAllocation = useTotalAllocation();
    const {
        data: {
            configuration: { workWeek, showAllocations, timesheetRAGsEnabled, capacityDefault },
            aggregatedUser: { user },
        },
    } = useInitialData();
    const styles = useTimeGridStyles();
    const resolvedWorkWeek = useMemo(() => workWeek ?? [], [workWeek]);
    const dateColumnStyles = useDateColumnStyles(dates, resolvedWorkWeek);
    const checkWorkDay = useMemo(() => isWorkDay(resolvedWorkWeek), [resolvedWorkWeek]);

    const [dialogOpen, setDialogOpen] = useState(false);

    const handleAllocationClick = useCallback(() => {
        setDialogOpen(true);
    }, []);

    const handleDialogClose = useCallback(() => {
        setDialogOpen(false);
    }, []);

    const availability = useMemo(
        () => user?.availability ?? user?.calendar?.dailyCapacity ?? capacityDefault ?? 0,
        [user?.availability, user?.calendar?.dailyCapacity, capacityDefault],
    );

    const ragEnabled = !!timesheetRAGsEnabled && availability > 0;

    const dateCells = useMemo(
        () =>
            dates.map(date => (
                <TotalDateCell
                    key={date}
                    total={dailyTotals[date] || bigZero}
                    className={mergeClasses(styles.cell, styles.cellHours, dateColumnStyles[date])}
                    showIndicator={ragEnabled && checkWorkDay(date)}
                    availability={availability}
                />
            )),
        [dates, dailyTotals, dateColumnStyles, ragEnabled, checkWorkDay, availability, styles.cell, styles.cellHours],
    );

    return (
        <div className={mergeClasses(styles.row, styles.rowTotal, showAllocations && styles.rowWithAllocation)} role="row">
            <div className={mergeClasses(styles.cell, styles.cellTotalLabel, styles.stickyHeaderCol1)} role="gridcell">
                Total
            </div>

            <div className={mergeClasses(styles.cell, styles.stickyHeaderCol2)} role="gridcell">
                {/* Empty */}
            </div>

            {dateCells}

            <div className={mergeClasses(styles.cell, styles.cellTotal)} role="gridcell">
                <strong>{grandTotal.toString()}</strong>
            </div>

            {showAllocations && (
                <div className={mergeClasses(styles.cell, styles.cellAllocation, styles.cellAllocationClickable)} onClick={handleAllocationClick}>
                    <InfoRegular />
                    &nbsp;{blankZero(totalAllocation)}
                </div>
            )}

            {dialogOpen && <TotalAllocationDialog open={dialogOpen} onClose={handleDialogClose} onProjectClick={onAllocationProjectClick} />}
        </div>
    );
});
